import { useQuery } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Info, Search, Users, FileText, Eye, CheckCircle2, AlertCircle, X, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import React, { useState } from "react";
import { Tag, Collapse, CollapseProps, Tabs, TabsProps } from "antd";
import { PersonalInfoStep } from "./steps/PersonalInfoStep";
import { AdditionalInfoStep } from "./steps/AdditionalInfoStep";
import { FamilyInfoStep } from "./steps/FamilyInfoStep";
import { BankInfoStep } from "./steps/BankInfoStep";
import { QualificacaoStep } from "./steps/QualificacaoStep";
import { DocumentsStep } from "./steps/DocumentsStep";
import { AddressStep } from "./steps/AddressStep";
import { Cliente } from "@/features/clientes/types/cliente";
import { useClienteForm } from "@/features/clientes/hooks/useClienteForm";
import { useClienteTypeDetection } from "@/features/clientes/hooks/useClienteTypeDetection";
import { ClientePreview } from "./form/ClientePreview";
import { ClientesService } from "../services/clientes.service";
import { unifiedServices } from "@/services/unifiedService";
import { useAuth } from "@/features/auth";
import { usePermissao } from "@/features/auth/hooks/usePermissao";
import { ClienteFormSimplificado } from "./ClienteFormSimplificado";
import { useProfileSwitcher } from "@/hooks/useProfileSwitcher";
import { ROLES } from "@/features/auth/types";
import { toast } from "sonner";
import { PipelineFormProvider } from "@/components/form/PipelineFormContext";

interface ClienteFormProps {
  formData?: Cliente;
  onSuccess?: (cliente: Cliente) => void;
  isDialog?: boolean;
}

// Hook para buscar documentos do cliente
const useClienteDocumentos = (clienteId: string | undefined, tenant_id: string | undefined) => {
  return useQuery({
    queryKey: ["documentos", clienteId],
    queryFn: async () => {
      if (!clienteId || !tenant_id) return [];
      const response = await ClientesService.listarDocumentos(clienteId);
      return response;
    },
    enabled: !!clienteId && !!tenant_id,
    staleTime: 1000 * 60 * 5, // Cache por 5 minutos
  });
};

// Função para obter URL pública do documento
const getDocumentUrl = async (path: string) => {
  if (!path) {
    return null;
  }

  try {
    return await unifiedServices.clientes.getDocumentUrl(path);
  } catch (error) {
    console.error("Erro ao obter URL do documento:", error);
    return null;
  }
};

export function ClienteForm({ formData, onSuccess, isDialog = false }: ClienteFormProps) {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const { getCurrentProfile } = useProfileSwitcher();
  const userRole = getCurrentProfile;
  const { isRelacionador } = usePermissao();

  // Se for Relacionador, mostrar formulário simplificado
  // IMPORTANTE: Associado e Super Admin devem ver o formulário COMPLETO
  const isRelacionadorRole = (isRelacionador || userRole === ROLES.RELACIONADOR || userRole === 'RELACIONADOR') && 
                             userRole !== ROLES.ASSOCIADO && 
                             userRole !== ROLES.SUPER_ADMIN;

  if (isRelacionadorRole) {
    return <ClienteFormSimplificado formData={formData} onSuccess={onSuccess} isDialog={isDialog} />;
  }


  const { data: documentos = [], isLoading: isLoadingDocs, refetch: refetchDocumentos } = useClienteDocumentos(
    formData?.id?.toString(),
    formData?.tenant_id?.toString()
  );

  const { podeCriarContrato } = usePermissao();

  const {
    form,
    onSubmit,
    onSaveDraft,
    onSaveAndCreateContract,
    isSubmitting,
    isSavingDraft,
    isSavingAndCreating,
    handleCPFSearch,
    dadosCliente,
    loadingCPF,
    currentRole,
    formConfig
  } = useClienteForm(formData, onSuccess);

  // Detectar tipo de cliente para mostrar labels corretos
  const { clienteType } = useClienteTypeDetection({ cpfCnpj: form.watch("cpf") });

  // Estado para controlar painéis de accordion abertos (apenas o primeiro aberto por padrão)
  const [openPanels, setOpenPanels] = useState<string[]>(['basicInfo']);
  // Estado da aba ativa quando o formulário é exibido em modo modal (isDialog)
  const [activeTabKey, setActiveTabKey] = useState<string>('basicInfo');

  // Mapa de campo para accordion (substitui fieldToTabMap)
  const fieldToAccordionMap: Record<string, string> = {
    nome: 'basicInfo', email: 'basicInfo', cpf: 'basicInfo',
    tel1: 'basicInfo', tel2: 'basicInfo', dataNascimento: 'basicInfo',
    rg: 'basicInfo', dataEmissaoRg: 'basicInfo',
    razaoSocial: 'basicInfo', nomeResponsavel: 'basicInfo',
    naturalidade: 'additionalInfo', nacionalidade: 'additionalInfo',
    atividadeDesenvolvida: 'additionalInfo', rendimentoTitular: 'additionalInfo',
    statusAtual: 'additionalInfo',
    cep: 'addressInfo', logradouro: 'addressInfo', cidade: 'addressInfo',
    estado: 'addressInfo', numero: 'addressInfo', bairro: 'addressInfo',
    nomeMae: 'familyInfo', estadoCivil: 'familyInfo', nomeConjuge: 'familyInfo',
    banco: 'bankInfo', agencia: 'bankInfo', conta: 'bankInfo',
    tipoBem: 'qualificacao', valorParcelaOrcamento: 'qualificacao',
    valorEntrada: 'qualificacao', tempoProcura: 'qualificacao', prioridade: 'qualificacao',
    documentoComFotoDoc: 'documents', residenciaDoc: 'documents',
  };

  // Campos rastreados por seção para status
  const sectionTrackedFields: Record<string, string[]> = {
    basicInfo: ['nome', 'cpf', 'tel1', 'email', 'dataNascimento'],
    additionalInfo: ['naturalidade', 'nacionalidade'],
    addressInfo: ['cep', 'logradouro', 'cidade', 'estado'],
    familyInfo: ['nomeMae', 'estadoCivil'],
    bankInfo: ['banco', 'agencia', 'conta'],
    qualificacao: ['tipoBem', 'valorParcelaOrcamento', 'tempoProcura', 'prioridade'],
    documents: [],
  };

  // Calcular status de cada seção
  const getSectionStatus = (sectionKey: string): 'empty' | 'partial' | 'complete' | 'error' => {
    const errors = form.formState.errors;
    const values = form.getValues();
    const trackedFields = sectionTrackedFields[sectionKey] || [];

    // Se tem erro nesta seção, mostrar erro
    if (trackedFields.some(field => field in errors)) {
      return 'error';
    }

    if (trackedFields.length === 0) return 'empty';

    const completedFields = trackedFields.filter(field => {
      const val = values[field as keyof typeof values];
      if (typeof val === 'boolean') return val === true;
      return val !== undefined && val !== null && val !== '' && val !== 0;
    });

    if (completedFields.length === 0) return 'empty';
    if (completedFields.length === trackedFields.length) return 'complete';
    return 'partial';
  };

  // Função para prevenir submissão automática
  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // Função para lidar com o clique no botão enviar
  const handleSubmitClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Validar todos os campos antes de submeter
    const isValid = await form.trigger();

    // Obter todos os erros de validação
    const errors = form.formState.errors;
    const errorCount = Object.keys(errors).length;

    if (!isValid || errorCount > 0) {
      // Mostrar mensagem de erro com detalhes
      if (errorCount > 0) {
        toast.error(`Por favor, corrija ${errorCount} erro(s) no formulário antes de salvar.`, {
          description: 'Verifique os campos destacados em vermelho.',
          duration: 5000,
        });

        // Encontrar o primeiro campo com erro e abrir accordion correspondente
        const errorFields = Object.keys(errors);
        const firstErrorField = errorFields[0];
        const targetPanel = fieldToAccordionMap[firstErrorField] || 'basicInfo';

        // Abrir a seção com erro (accordion no formulário completo, aba no modal)
        if (isDialog) {
          setActiveTabKey(targetPanel);
        } else {
          setOpenPanels([targetPanel]);
        }

        // Aguardar um pouco para o accordion aparecer antes de focar no campo
        setTimeout(() => {
          const errorElement = document.querySelector(`[name="${firstErrorField}"]`) ||
            document.querySelector(`[id="${firstErrorField}"]`);
          if (errorElement) {
            errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            (errorElement as HTMLElement).focus();
          }
        }, 300);
      } else {
        toast.error('Por favor, preencha todos os campos obrigatórios.');
      }

      return;
    }

    // Se passou na validação, submeter o formulário
    form.handleSubmit(onSubmit)();
  };



  // Callback para atualizar lista de documentos após exclusão
  const handleDocumentDeleted = async () => {
    await refetchDocumentos();
  };

  // Calcular progresso do formulário de forma mais granular
  const calculateProgress = () => {
    const values = form.getValues();
    // Campos importantes para acompanhar o progresso em todas as etapas
    const fieldsToTrack = [
      'nome', 'cpf', 'tel1', 'email', 'dataNascimento', // Informações Pessoais
      'cep', 'logradouro', 'cidade', 'estado', // Endereço
      'nomeMae', 'estadoCivil', // Família
      'banco', 'agencia', 'conta', // Banco
      'valorParcelaOrcamento', 'tipoBem', 'prioridade', 'tempoProcura' // Qualificação
    ];
    
    const completedFields = fieldsToTrack.filter(field => {
      const val = values[field as keyof typeof values];
      if (typeof val === 'boolean') return val === true;
      return val !== undefined && val !== null && val !== '' && val !== 0;
    });
    
    return Math.round((completedFields.length / fieldsToTrack.length) * 100);
  };

  // Estados para busca por nome
  const [searchTerm, setSearchTerm] = useState('');
  const [searchMode, setSearchMode] = useState<'cpf' | 'nome'>('cpf');
  const [searchResults, setSearchResults] = useState<Cliente[]>([]);
  const [showSearchResults, setShowSearchResults] = useState(false);

  // Buscar clientes por nome ou CPF
  const { data: searchData, isLoading: isSearching } = useQuery({
    queryKey: ['clientes-search', searchTerm, searchMode, profile?.tenant_id],
    queryFn: async () => {
      if (!searchTerm || searchTerm.length < 3) return { data: [], total: 0 };

      const response = await ClientesService.listarClientes(1, 10, searchTerm, profile?.tenant_id?.toString());
      return response;
    },
    enabled: searchTerm.length >= 3 && !formData?.id,
    staleTime: 1000 * 60 * 5, // Cache por 5 minutos
  });

  // Atualizar resultados quando a busca mudar
  React.useEffect(() => {
    if (searchData && searchData.data) {
      // Filtrar por modo de busca
      let filtered: Cliente[] = [];

      if (searchMode === 'cpf') {
        const documentoLimpo = searchTerm.replace(/\D/g, '');
        filtered = searchData.data.filter(cliente => {
          const cpfLimpo = cliente.cpf?.replace(/\D/g, '') || '';
          return cpfLimpo.includes(documentoLimpo);
        });
      } else {
        // Busca por nome
        filtered = searchData.data.filter(cliente =>
          cliente.nome?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }

      setSearchResults(filtered);
      setShowSearchResults(filtered.length > 0);
    } else {
      setSearchResults([]);
      setShowSearchResults(false);
    }
  }, [searchData, searchMode, searchTerm]);

  // Handler para busca por CPF/CNPJ ou Nome
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);

    // Detectar se é CPF/CNPJ ou nome
    const documentoLimpo = value.replace(/\D/g, '');
    if (documentoLimpo.length >= 11 || documentoLimpo.length === 14) {
      setSearchMode('cpf');
      // Se for CPF completo, usar busca específica
      if (documentoLimpo.length === 11 || documentoLimpo.length === 14) {
        handleCPFSearch(documentoLimpo);
      }
    } else if (value.length >= 3) {
      setSearchMode('nome');
      setShowSearchResults(true);
    } else {
      setSearchResults([]);
      setShowSearchResults(false);
    }
  };

  // Selecionar cliente da busca
  const handleSelectCliente = (cliente: Cliente) => {
    // Preencher formulário com dados do cliente
    form.setValue('nome', cliente.nome || '');
    form.setValue('cpf', cliente.cpf || '');
    form.setValue('email', cliente.email || '');
    form.setValue('tel1', cliente.tel1 || '');
    form.setValue('tel2', cliente.tel2 || '');
    form.setValue('cidade', cliente.cidade || '');
    form.setValue('estado', cliente.estado || '');
    form.setValue('cep', cliente.cep || '');
    form.setValue('logradouro', cliente.logradouro || '');
    form.setValue('numero', cliente.numero || '');
    form.setValue('bairro', cliente.bairro || '');

    setSearchTerm('');
    setSearchResults([]);
    setShowSearchResults(false);

    toast.success(`Cliente "${cliente.nome}" selecionado. Dados preenchidos automaticamente.`);
  };

  // Handler para busca por CPF/CNPJ (mantido para compatibilidade)
  const handleSaveDraft = () => {
    const formData = form.getValues();
    onSaveDraft(formData);
  };

  // Estados para pré-visualização
  const [showPreview, setShowPreview] = useState(false);

  // Estado para modal de busca de cliente
  const [showSearchModal, setShowSearchModal] = useState(false);

  const handlePreview = () => {
    setShowPreview(true);
  };

  // Conteúdo de cada seção do formulário, compartilhado entre o modo accordion (página cheia)
  // e o modo tabs (dentro do modal), para não duplicar os componentes de step.
  const sections: Array<{ key: string; title: string; subtitle: string; tabLabel: string; content: React.ReactNode }> = [
    {
      key: 'basicInfo',
      title: 'Informações Pessoais',
      subtitle: 'Nome, CPF, telefone e email',
      tabLabel: 'Pessoal',
      content: (
        <PersonalInfoStep
          form={form}
          clienteId={formData?.id?.toString()}
          onSearchClick={() => setShowSearchModal(true)}
          enableSearch={formConfig.enableQuickActions && !formData?.id}
        />
      ),
    },
    {
      key: 'additionalInfo',
      title: 'Informações Adicionais',
      subtitle: 'Naturalidade, nacionalidade',
      tabLabel: 'Adicional',
      content: <AdditionalInfoStep form={form} />,
    },
    {
      key: 'addressInfo',
      title: 'Endereço',
      subtitle: 'CEP, logradouro, cidade e estado',
      tabLabel: 'Endereço',
      content: <AddressStep form={form} />,
    },
    {
      key: 'familyInfo',
      title: 'Informações Familiares',
      subtitle: 'Mãe, estado civil e cônjuge',
      tabLabel: 'Família',
      content: <FamilyInfoStep form={form} />,
    },
    {
      key: 'bankInfo',
      title: 'Dados Bancários',
      subtitle: 'Banco, agência e conta',
      tabLabel: 'Banco',
      content: <BankInfoStep form={form} />,
    },
    {
      key: 'qualificacao',
      title: 'Qualificação',
      subtitle: 'Bem, valor parcela e prioridade',
      tabLabel: 'Qualificação',
      content: <QualificacaoStep form={form} />,
    },
    {
      key: 'documents',
      title: 'Documentos',
      subtitle: 'Comprovante de identidade e residência',
      tabLabel: 'Documentos',
      content: (
        <DocumentsStep
          form={form}
          documentos={documentos}
          isLoadingDocs={isLoadingDocs}
          getDocumentUrl={getDocumentUrl}
          isEditing={!!formData?.id}
          clienteId={formData?.id?.toString()}
          onDocumentDeleted={handleDocumentDeleted}
        />
      ),
    },
  ];

  const renderStatusTag = (sectionKey: string) => {
    const status = getSectionStatus(sectionKey);
    if (status === 'complete') return <Tag color="green" style={{ fontSize: '11px' }}>Preenchido</Tag>;
    if (status === 'partial') return <Tag color="orange" style={{ fontSize: '11px' }}>Parcial</Tag>;
    if (status === 'error') return <Tag color="red" style={{ fontSize: '11px' }}>Erro</Tag>;
    return <Tag style={{ fontSize: '11px' }}>Vazio</Tag>;
  };

  const renderStatusDot = (sectionKey: string) => {
    const status = getSectionStatus(sectionKey);
    const color = status === 'complete' ? '#52c41a' : status === 'partial' ? '#faad14' : status === 'error' ? '#ff4d4f' : null;
    if (!color) return null;
    return <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ backgroundColor: color }} />;
  };

  const collapseItems: CollapseProps['items'] = sections.map((section) => ({
    key: section.key,
    label: (
      <div className="flex items-center justify-between w-full">
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold">{section.title}</span>
          <span className="text-xs text-gray-500">{section.subtitle}</span>
        </div>
        <div onClick={(e) => e.stopPropagation()} className="ml-4">
          {renderStatusTag(section.key)}
        </div>
      </div>
    ),
    children: <div className="pt-2">{section.content}</div>,
  }));

  const tabItems: TabsProps['items'] = sections.map((section) => ({
    key: section.key,
    label: (
      <span className="flex items-center gap-2">
        {section.tabLabel}
        {renderStatusDot(section.key)}
      </span>
    ),
    children: <div className="pt-3">{section.content}</div>,
  }));

  return (
    <div className="w-full">
      {/* Header personalizado por papel */}
      {!isDialog && (
        <div className="bg-white rounded-t-xl border border-b-0 p-6 mb-0">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/clientes')}
                className="p-0 hover:opacity-80 transition-opacity"
              >
                <ArrowLeft className="h-5 w-5 text-blue-600" />
              </button>
              <Users className="h-6 w-6 text-mendes-blue" />
              <div>
                <h1 className="text-2xl font-bold">{formConfig.title}</h1>
                <p className="text-muted-foreground">{formConfig.description}</p>
              </div>
            </div>
            <Badge variant="secondary">
              {currentRole.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase())}
            </Badge>
          </div>

          {/* Barra de progresso para vendedores */}
          {formConfig.showProgressBar && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Progresso do cadastro</span>
                <span>{calculateProgress()}%</span>
              </div>
              <Progress 
                value={calculateProgress()} 
                className="h-2" 
                indicatorClassName="bg-green-500" 
              />
            </div>
          )}
        </div>
      )}

      <Card className={isDialog ? "border-0 shadow-none" : "rounded-t-none"}>
        <div className={isDialog ? "p-2 md:p-3" : "p-4 md:p-6"}>

          {/* Barra de progresso global */}
          {!isDialog && (
          <div className="mb-8">
            <div className="flex justify-between text-sm mb-2">
              <span className="font-medium text-gray-700">Progresso: {calculateProgress()}%</span>
              <span className="text-gray-500">{calculateProgress()} / 100</span>
            </div>
            <Progress
              value={calculateProgress()}
              className="h-2"
              indicatorClassName="bg-blue-600"
            />
          </div>
          )}

          <PipelineFormProvider variant={isDialog ? "pipeline" : "default"}>
          <Form {...form}>
            <form autoComplete="off" onSubmit={handleFormSubmit} className={`space-y-4 ${isDialog ? 'pb-4' : 'pb-24'}`} noValidate>
              {isDialog ? (
                <Tabs
                  activeKey={activeTabKey}
                  onChange={setActiveTabKey}
                  items={tabItems}
                />
              ) : (
                <Collapse
                  accordion
                  activeKey={openPanels}
                  onChange={(keys) => setOpenPanels(keys as string[])}
                  className="rounded-[16px] overflow-hidden border border-[rgba(15,118,110,0.08)]"
                  items={collapseItems}
                />
              )}

            </form>
          </Form>
          </PipelineFormProvider>
        </div>

        {/* Rodapé com botões de ação */}
        <div className={`border-t border-border bg-card flex flex-col sm:flex-row items-center gap-3 ${isDialog ? 'p-3 justify-end' : 'p-4 justify-between'}`}>
          {!isDialog && (
          <Button
            type="button"
            variant="outline"
            onClick={handleSaveDraft}
            disabled={isSubmitting || isSavingDraft}
            className="flex items-center gap-2 w-full sm:w-auto"
          >
            {isSavingDraft ? (
              <>
                <Loader2 className="animate-spin h-4 w-4" />
                Salvando...
              </>
            ) : (
              <>
                <FileText className="h-4 w-4" />
                Salvar Rascunho
              </>
            )}
          </Button>
          )}

          <div className={`flex gap-2 ${isDialog ? 'w-full' : 'w-full sm:w-auto'}`}>
            {!isDialog && (
            <>
            <Button
              type="button"
              variant="outline"
              onClick={handlePreview}
              disabled={isSubmitting}
              className="flex items-center gap-2 flex-1 sm:flex-none"
            >
              <Eye className="h-4 w-4" />
              Pré-visualizar
            </Button>

            {formConfig.enableQuickActions && podeCriarContrato && (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  form.handleSubmit(onSaveAndCreateContract)();
                }}
                disabled={isSubmitting || isSavingAndCreating}
                className="bg-green-50 border-green-200 text-green-700 hover:bg-green-100 flex-1 sm:flex-none"
              >
                {isSavingAndCreating ? (
                  <>
                    <Loader2 className="animate-spin h-4 w-4" />
                    Salvando...
                  </>
                ) : (
                  'Salvar e Criar Contrato'
                )}
              </Button>
            )}
            </>
            )}

            <Button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSubmitClick(e);
              }}
              className={`bg-blue-600 hover:bg-blue-700 text-white ${isDialog ? 'w-full' : 'flex-1 sm:flex-none'}`}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="animate-spin h-4 w-4" />
                  Salvando...
                </>
              ) : (
                isDialog ? 'Salvar alterações' : 'Salvar Cliente'
              )}
            </Button>
          </div>
        </div>
      </Card>

      {/* Modal de Busca de Cliente */}
      {showSearchModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center">
          <Card className="w-full max-w-md mx-4">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">Buscar Cliente Existente</h2>
                <button
                  onClick={() => setShowSearchModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="relative">
                  <Input
                    placeholder="Digite o nome ou CPF/CNPJ do cliente"
                    value={searchTerm}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    onFocus={() => setShowSearchResults(true)}
                    className="pr-10"
                  />
                  <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                </div>

                <p className="text-xs text-gray-600">
                  Busque por nome (mínimo 3 caracteres) ou CPF/CNPJ completo
                </p>

                {/* Resultados da busca */}
                {showSearchResults && searchResults.length > 0 && (
                  <div className="border border-gray-200 rounded-lg max-h-64 overflow-y-auto">
                    {searchResults.map((cliente) => (
                      <div
                        key={cliente.id}
                        onClick={() => {
                          handleSelectCliente(cliente);
                          setShowSearchModal(false);
                        }}
                        className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                            <Users className="h-4 w-4 text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-foreground text-sm truncate">{cliente.nome}</p>
                            <p className="text-xs text-muted-foreground truncate">
                              {cliente.cpf && `${cliente.cpf} • `}
                              {cliente.email || cliente.tel1}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {searchTerm.length >= 3 && searchResults.length === 0 && !isSearching && (
                  <div className="p-3 text-sm text-gray-600 bg-gray-50 rounded-lg">
                    Nenhum cliente encontrado com "{searchTerm}"
                  </div>
                )}

                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowSearchModal(false)}
                  className="w-full"
                >
                  Fechar
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Modal de Pré-visualização */}
      <ClientePreview
        open={showPreview}
        onOpenChange={setShowPreview}
        formData={form.getValues()}
        onConfirm={() => {
          setShowPreview(false);
          form.handleSubmit(onSubmit)();
        }}
        onEdit={() => setShowPreview(false)}
        documentos={documentos}
        getDocumentUrl={getDocumentUrl}
      />
    </div>
  );
}
